import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import configData from '../../configure';
import './StudentImport.css';
import { useAuth } from "../../AuthContext";  // Import useAuth
import { toast } from 'react-toastify';

function StudentBulkImport({ orgId: propOrgId }) {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [importSummary, setImportSummary] = useState(null); 
  
  const navigate = useNavigate();
  const location = useLocation();
  const { axios, token, orgId: contextOrgId } = useAuth();
  
  const orgId = propOrgId || contextOrgId || location.state?.orgId;
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(location.state?.selectedClass || '');

  // Fetch classes when orgId changes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!orgId) return;
      try {
        const { data } = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);
        setClasses(data);
        // If current selection is not in new classes, clear it
        if (data && !data.find(c => c.classId === selectedClass)) {
          setSelectedClass('');
        }
      } catch (err) {
        console.error("Failed to load classes", err);
      }
    };
    fetchClasses();
  }, [orgId, axios]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (selectedFile.type !== 'text/csv' &&
        !selectedFile.name.toLowerCase().endsWith('.csv')) {
        toast.error('Please upload a valid CSV file.');
        setUploadStatus('error');
        return;
      }
      setFile(selectedFile);
      setUploadStatus('idle');
    }
  };

  const handleUpload = async () => {
    if (!file || !orgId || !selectedClass) return;

    setUploadStatus('uploading');
    setImportSummary(null);

    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      const csvText = e.target.result;
      const rows = csvText.split('\n').slice(1);

      // Parse all valid rows first
      const studentsToProcess = rows
        .map((row, index) => {
          if (!row.trim()) return null;
          const values = row.split(',').map(v => v.trim());
          // Basic validation
          // Map new CSV format: First Name,Last Name,Father Name,Father Occupation,Mother Name,Mother Occupation,Caste,Single Parent,Mobile No
          const [
            firstName, lastName, fatherName, fatherOccupation,
            motherName, motherOccupation, caste, singleParent, mobileNo
          ] = values;

          // Auto-generate missing mandatory fields
          const generatedEmail = `${mobileNo || Date.now()}@egurukul.com`;
          const generatedAdmissionId = `ADM${Date.now()}${index}`;
          const generatedRollNumber = `RN${Date.now()}${index}`;

          return {
            originalRow: index + 2,
            email: generatedEmail,
            payload: {
              user: {
                creationTime: new Date().toISOString(),
                createdBy: "admin",
                lastUpdatedTime: new Date().toISOString(),
                lastUpdatedBy: "admin",
                orgId,
                userType: "STUDENT",
                email: generatedEmail,
                phoneNumber: mobileNo,
                firstName,
                lastName,
                gender: "N/A",
                nationality: "Indian",
                category: caste || "N/A",
                addressDetails: [],
                userStatus: "ACTIVE"
              },
              studentInfo: {
                creationTime: new Date().toISOString(),
                createdBy: "admin",
                lastUpdatedTime: new Date().toISOString(),
                lastUpdatedBy: "admin",
                classId: selectedClass,
                admissionId: generatedAdmissionId,
                rollNumber: generatedRollNumber,
                enrollmentStatus: "ENROLLED",
                dateOfJoining: new Date().toISOString(),
                admissionType: "REGULAR",
                fatherName,
                fatherOccupation,
                motherName,
                motherOccupation,
                singleParent: (singleParent && singleParent.toLowerCase() === 'yes')
              }
            }
          };
        })
        .filter(Boolean);

      const stats = {
        total: studentsToProcess.length,
        success: 0,
        duplicates: 0,
        failed: 0,
        errors: []
      };

      // Process each student one by one
      for (const item of studentsToProcess) {
        const { payload, email, originalRow } = item;

        // Backend Creation
        try {
          const response = await axios.post(
            `${configData.REGISTER_USER}`,
            payload,
            { headers: { 'accept': 'application/json', 'Content-Type': 'application/json' } }
          );

          if (response.status === 200 || response.status === 201) {
            stats.success++;
            // Trigger Email (Fire and forget)
            sendWelcomeEmail(response.data?.userId || response.data?.user?.userId, email);
          } else {
            throw new Error(`Server returned status ${response.status}`);
          }

        } catch (apiError) {
          console.error(`API Error for ${email}:`, apiError);
          const errorMsg = apiError.response?.data?.message || apiError.message || "Backend creation failed";

          if (errorMsg.toLowerCase().includes('already exists') || errorMsg.toLowerCase().includes('duplicate')) {
            stats.duplicates++;
            stats.errors.push({
              row: originalRow,
              email: email,
              reason: "User already exists (Duplicate)"
            });
          } else {
            stats.failed++;
            stats.errors.push({
              row: originalRow,
              email: email,
              reason: errorMsg
            });
          }
        }
      }

      setImportSummary(stats);
      setUploadStatus('complete');
      
      if (stats.success > 0) {
        toast.success(`Successfully imported ${stats.success} student(s).`);
      }
      if (stats.failed > 0) {
        toast.error(`Failed to import ${stats.failed} student(s). Check the summary for details.`);
      }
    };

    fileReader.onerror = () => {
      toast.error("Failed to read file.");
      setUploadStatus('error');
    };

    fileReader.readAsText(file);
  };

  const sendWelcomeEmail = async (userId, email) => {
    if (!userId || !token) return;
    try {
      const emailData = {
        subject: "Welcome to Egurukul - Your Account Details",
        messageBody: `Welcome to Egurukul!\n\nYour account has been successfully created.\n\nLogin Details:\nUsername: ${email}\nPassword: Sevabharathi@123\n\nPlease login and change your password immediately.`,
        userType: "STUDENT",
        specificUserIds: [userId],
      };
      const emailFormData = new FormData();
      emailFormData.append('request', new Blob([JSON.stringify(emailData)], { type: 'application/json' }));

      await axios.post(configData.SEND_EMAIL_NOTIFICATION, emailFormData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      console.warn(`Email failed for ${email}`, e);
    }
  };

  const handleViewStudents = () => {
    navigate('/students', {
      state: {
        selectedClass: selectedClass
      }
    });
  };

  const handleBack = () => {
    navigate('/students');
  };
  const generateCSVTemplate = () => {
    const headers = "First Name,Last Name,Father Name,Father Occupation,Mother Name,Mother Occupation,Caste,Single Parent,Mobile No";
    const sampleRow1 = "BANGARA,DAATHRI SREE,Bhaskar,Board Maker,Sujatha,Housewife,SC Madiga,No,9381554946";
    const sampleRow2 = "BANOTH,AIKYA SRI,Ravi,Electrician,Meena,Housewife,ST Banjara,No,9963698338";

    return `${headers}\n${sampleRow1}\n${sampleRow2}`;
  };

  return (
    <div className="import-container">
      <div className="import-card">
        <div className="header-section">
          <button className="back-button" onClick={handleBack}>
            &larr; Back
          </button>
          <h2 className="title">Bulk Student Import</h2>
        </div>

        <div className="content-section">
          <div className="upload-area">
            <label className="file-input-label">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden-input"
              />
              <span className="upload-button">
                📁 Choose CSV File
              </span>
            </label>
            {file && <p className="file-name">Selected: {file.name}</p>}
          </div>



          <div className="class-selection-area" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Target Class:</label>
            <select 
              value={selectedClass} 
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="">-- Select Class --</option>
              {classes.map(cls => {
                const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
                return (
                  <option key={cls.classId} value={cls.classId}>
                    {section ? `${cls.className} ${section}` : `${cls.className || ''}`}
                  </option>
                );
              })}
            </select>
          </div>

          <button
            className={`import-button ${uploadStatus === 'uploading' ? 'loading' : ''}`}
            onClick={handleUpload}
            disabled={!file || uploadStatus === 'uploading' || !selectedClass}
          >
            {uploadStatus === 'uploading' ? (
              <span className="spinner"></span>
            ) : (
              'Start Import'
            )}
          </button>

          {uploadStatus === 'complete' && importSummary && (
            <div className="summary-section">
              <div className="summary-card">
                <h3>Import Summary</h3>
                <div className="summary-stats">
                  <div className="stat-item warning">
                    <span className="count">{importSummary.duplicates}</span>
                    <span className="label">Duplicates (Skipped)</span>
                  </div>
                  <div className="stat-item error">
                    <span className="count">{importSummary.failed}</span>
                    <span className="label">Failed</span>
                  </div>
                </div>

                {importSummary.errors.length > 0 && (
                  <div className="error-details">
                    <h4>Error Details:</h4>
                    <ul>
                      {importSummary.errors.map((err, idx) => (
                        <li key={idx}>
                          <strong>Row {err.row} ({err.email}):</strong> {err.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="view-button"
                  onClick={handleViewStudents}
                  style={{ marginTop: '20px' }}
                >
                  Done & View List
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="csv-instructions">
          <h3>CSV Format Requirements</h3>
          <p>
            Download
            <a
              href={`data:text/csv;charset=utf-8,${encodeURIComponent(generateCSVTemplate())}`}
              download="StudentsTemplate.csv"
            >
              sample CSV template
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default StudentBulkImport;
